// Marketing's tables and rows (BL-189; were AdsViews' AdsSummary, AdsFunnel, SpendThisMonth,
// CampaignTable, CallReasons, CallsBySource, Conversions, AdsTrend and AdChangeList, pages/ux-full
// screens/ads.js): what each says, in words, with no drawing of its own.
// - How the ads did (reports.campaigns by channel): the period's five figures, and each channel's row.
// - From call to invoice (the paid rows) and the month's spend by platform (adSpend.list): bars.
// - The campaigns by return (reports.campaigns by campaign beside campaigns.list) and why they called
//   (reports.callReasons, with its total): tables.
// - Today's calls (calls.recent): the time, who called (opening the customer), the reason and what came
//   of it; what went to Google (adConversions.list); the ads over time (reports.campaigns by day or
//   week, with its total); and the suggested ad changes (adChanges.list).
import type { DataCell, DataColumn } from '../components/DataTable/DataTable.tsx';
import type { FigureItem } from '../components/Figures/Figures.tsx';
import type { KeyValueItem } from '../components/KeyValue/KeyValue.tsx';
import type { LabelKind } from '../components/Label/Label.tsx';
import type { Row, RowTone } from '../components/RowList/RowList.tsx';
import {
  at,
  callOutcome,
  campaignWord,
  changeState,
  channelWord,
  CONVERSION,
  CONVERSION_WORDS,
  paid,
  periodWord,
  since,
} from '../components/AdsViews.tsx';
import { clock, dayOf, money, perDollar, phone, plural, rate } from '../format.ts';
import type {
  AdChangeRow,
  AdsTrendProps,
  CallReasonsProps,
  CallsBySourceProps,
  CampaignRow,
  CampaignsReport,
  CampaignTableProps,
  ConversionsProps,
  SpendProps,
} from '../schemas.ts';

/** A Label's kind as a table's or a row's state word: now is ink. */
const toneOf = (k: LabelKind): RowTone => (k === 'now' ? 'ink' : k);

/** What a row spent: its money (estimated said under it), or no ad cost, muted. */
const spentCell = (r: Pick<CampaignRow, 'spendCents' | 'spendEstimated'>): DataCell =>
  r.spendCents
    ? { text: money(r.spendCents), ...(r.spendEstimated ? { lines: [{ text: 'estimated' }] } : {}) }
    : { text: 'no ad cost', look: 'muted' };
const count = (n: number): DataCell => ({ text: String(n) });
const moneyOrDash = (c: number | null): DataCell => ({ text: c === null ? '—' : money(c) });

// ── How the ads did ──

/** The period's five figures: spent, calls (from ads), booked, completed and revenue. */
export function adsFigures(p: CampaignsReport): FigureItem[] {
  const t = p.total;
  const fromAds = paid(p.rows).reduce((s, r) => s + r.calls, 0);
  return [
    {
      label: 'Spent on ads',
      value: money(t.spendCents),
      ...(t.spendEstimated ? { sub: 'some of it estimated' } : {}),
    },
    { label: 'Calls', value: String(t.calls), sub: `${fromAds} from ads` },
    { label: 'Booked', value: String(t.booked), sub: `${rate(t.leadConversionRate)} of leads` },
    { label: 'Completed', value: String(t.jobsCompleted), sub: `${t.invoicedJobs} invoiced` },
    { label: 'Revenue', value: money(t.revenueCents), sub: 'invoiced, before tax' },
  ];
}

export type ReportLine = CampaignRow & { id: string };

/** reports.campaigns' rows with an id each, most revenue first (then most calls). */
export const channelLines = (p: CampaignsReport): ReportLine[] =>
  [...p.rows]
    .sort((a, b) => b.revenueCents - a.revenueCents || b.calls - a.calls)
    .map((r) => ({ ...r, id: r.key ?? 'none' }));

/** Each channel's columns: what it spent, the calls and leads it brought, and what came of them. */
export const channelColumns: DataColumn<ReportLine>[] = [
  { id: 'channel', label: 'Channel', rowHeader: true, cell: (r) => ({ text: channelWord(r.channel) }) },
  { id: 'spent', label: 'Spent', align: 'end', cell: spentCell },
  { id: 'calls', label: 'Calls', align: 'end', cell: (r) => count(r.calls) },
  { id: 'leads', label: 'Leads', align: 'end', cell: (r) => count(r.leads) },
  { id: 'booked', label: 'Booked', align: 'end', cell: (r) => count(r.booked) },
  { id: 'completed', label: 'Completed', align: 'end', cell: (r) => count(r.jobsCompleted) },
  { id: 'revenue', label: 'Revenue', align: 'end', cell: (r) => ({ text: money(r.revenueCents) }) },
  {
    id: 'per',
    label: 'For each $1',
    align: 'end',
    cell: (r) => ({ text: perDollar(r.revenueCents, r.spendCents) }),
  },
];

/** From call to invoice: the paid rows' calls, leads, bookings, completions and invoices, each a bar
 * against the calls, its share of the step before under it; null with no calls from the ads. */
export function funnelRows(rows: readonly CampaignRow[]): Row[] | null {
  const ads = paid(rows);
  const s = (k: 'calls' | 'leads' | 'booked' | 'jobsCompleted' | 'invoicedJobs') =>
    ads.reduce((a, r) => a + r[k], 0);
  const steps: [string, number][] = [
    ['Calls', s('calls')],
    ['Leads', s('leads')],
    ['Booked', s('booked')],
    ['Completed', s('jobsCompleted')],
    ['Invoiced', s('invoicedJobs')],
  ];
  const first = steps[0]![1];
  if (!first) return null;
  return steps.map(([label, n], i) => {
    const before = i ? steps[i - 1]! : null;
    return {
      id: label,
      name: label,
      bar: { value: n, max: first, label },
      end: { figure: String(n) },
      ...(before && before[1]
        ? { lines: [{ text: `${Math.round((n / before[1]) * 100)} % of ${before[0].toLowerCase()}` }] }
        : {}),
    };
  });
}

const daysIn = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;

/** The month's spend up to today (each entry spread evenly over its days, as the report spreads it). */
export interface SpendMonth {
  totalCents: number;
  /** Days gone, and in the month. */
  gone: number;
  days: number;
  /** Where the month ends at this rate. */
  projectedCents: number;
  /** Each platform's share, most first, and whether any of it is estimated. */
  platforms: { channel: string; cents: number; estimated: boolean }[];
  /** What's spent on ad campaigns not linked to a campaign yet, and their names. */
  unlinkedCents: number;
  unlinked: string[];
}

export function spendMonth(p: SpendProps): SpendMonth {
  const upTo = p.today < p.to ? p.today : p.to;
  const share = (e: SpendProps['entries'][number]) => {
    const end = upTo < e.to ? upTo : e.to;
    return end < e.from ? 0 : Math.round((e.amountCents * daysIn(e.from, end)) / daysIn(e.from, e.to));
  };
  const totalCents = p.entries.reduce((a, e) => a + share(e), 0);
  const gone = daysIn(p.from, upTo);
  const days = daysIn(p.from, p.to);
  const byPlatform = new Map<string, { cents: number; estimated: boolean }>();
  for (const e of p.entries) {
    const b = byPlatform.get(e.channel) ?? { cents: 0, estimated: false };
    byPlatform.set(e.channel, { cents: b.cents + share(e), estimated: b.estimated || e.estimated });
  }
  const unlinked = p.entries.filter((e) => !e.campaign);
  return {
    totalCents,
    gone,
    days,
    projectedCents: Math.round((totalCents / gone) * days),
    platforms: [...byPlatform]
      .sort((a, b) => b[1].cents - a[1].cents)
      .map(([channel, b]) => ({ channel, ...b })),
    unlinkedCents: unlinked.reduce((a, e) => a + share(e), 0),
    unlinked: unlinked.map((e) => e.platformCampaign?.name ?? channelWord(e.channel)),
  };
}

/** Each platform's spend this month as a bar against the most, an estimate said under it. */
export function spendRows(m: SpendMonth): Row[] {
  const most = Math.max(1, ...m.platforms.map((b) => b.cents));
  return m.platforms.map((b) => ({
    id: b.channel,
    name: channelWord(b.channel),
    bar: { value: b.cents, max: most, label: channelWord(b.channel) },
    end: { figure: money(b.cents) },
    ...(b.estimated ? { lines: [{ text: 'estimated: an invoice spread over its days' }] } : {}),
  }));
}

// ── Which campaigns work? ──

/** The campaigns, best return first (then revenue, then calls). */
export const campaignLines = (p: CampaignsReport): ReportLine[] =>
  [...p.rows]
    .sort((a, b) => (b.roas ?? -1) - (a.roas ?? -1) || b.revenueCents - a.revenueCents || b.calls - a.calls)
    .map((r) => ({ ...r, id: r.key ?? 'none' }));

/** The paid campaigns together, in a sentence: what they cost and brought, and its gross profit. */
export function campaignsLine(p: CampaignsReport): string | null {
  const ads = paid(p.rows);
  const spend = ads.reduce((a, r) => a + r.spendCents, 0);
  if (!spend) return null;
  const revenue = ads.reduce((a, r) => a + r.revenueCents, 0);
  const gp = ads.reduce((a, r) => a + (r.grossProfitCents ?? 0), 0);
  const margins = 'grossProfitCents' in p.total;
  return `The paid ones together: ${money(spend)} brought ${money(revenue)}, ${perDollar(revenue, spend)} for each $1${margins ? `, ${perDollar(gp, spend)} of it gross profit` : ''}.`;
}

/** Each campaign's columns: its channel and numbers under its name; gross profit only for whoever the
 * server gave it to (finance.margins.view). */
export function campaignColumns(p: CampaignTableProps): DataColumn<ReportLine>[] {
  const margins = 'grossProfitCents' in p.total;
  const info = (key: string | null) => p.campaigns.find((c) => c.id === key);
  return [
    {
      id: 'campaign',
      label: 'Campaign',
      rowHeader: true,
      cell: (r) => {
        const c = info(r.key);
        const sub = [
          r.key ? channelWord(r.channel) : 'Calls on no tracking number',
          ...(c?.numbers.map((n) => n.text) ?? []),
          ...(c && !c.active ? ['retired'] : []),
        ];
        return { text: r.key ? r.label : 'No campaign', lines: [{ text: sub.join(', ') }] };
      },
    },
    { id: 'spent', label: 'Spent', align: 'end', cell: spentCell },
    { id: 'calls', label: 'Calls', align: 'end', cell: (r) => count(r.calls) },
    { id: 'booked', label: 'Booked', align: 'end', cell: (r) => count(r.booked) },
    { id: 'cpb', label: 'Cost a booking', align: 'end', cell: (r) => moneyOrDash(r.costPerBookedJobCents) },
    { id: 'completed', label: 'Completed', align: 'end', cell: (r) => count(r.jobsCompleted) },
    { id: 'revenue', label: 'Revenue', align: 'end', cell: (r) => ({ text: money(r.revenueCents) }) },
    {
      id: 'per',
      label: 'For each $1',
      align: 'end',
      cell: (r) => ({ text: perDollar(r.revenueCents, r.spendCents) }),
    },
    ...(margins
      ? [
          {
            id: 'gp',
            label: 'Gross profit for each $1',
            align: 'end' as const,
            cell: (r: ReportLine): DataCell => ({
              text: r.grossProfitCents == null ? '—' : perDollar(r.grossProfitCents, r.spendCents),
            }),
          },
        ]
      : []),
  ];
}

type Reason = CallReasonsProps['total'];
export type ReasonLine = Reason & { id: string };

/** The reasons, most calls first, and the total ("All calls taken"). */
export function reasonLines(p: CallReasonsProps): { rows: ReasonLine[]; total: ReasonLine } {
  return {
    rows: [...p.rows]
      .sort((a, b) => b.taken - a.taken)
      .map((r) => ({ ...r, id: `${r.key ?? 'none'}-${r.label}` })),
    total: { ...p.total, id: 'total', label: 'All calls taken' },
  };
}

/** The reasons' columns: calls, leads, bookings, what wasn't booked, and those with no reason yet (red). */
export const reasonColumns: DataColumn<ReasonLine>[] = [
  { id: 'reason', label: 'Reason', rowHeader: true, cell: (r) => ({ text: r.label }) },
  { id: 'taken', label: 'Calls', align: 'end', cell: (r) => count(r.taken) },
  { id: 'leads', label: 'Leads', align: 'end', cell: (r) => count(r.leads) },
  { id: 'booked', label: 'Booked', align: 'end', cell: (r) => count(r.booked) },
  { id: 'unbooked', label: 'Leads not booked', align: 'end', cell: (r) => count(r.unbookedLeads) },
  { id: 'excused', label: 'Excused', align: 'end', cell: (r) => count(r.excused) },
  {
    id: 'notset',
    label: 'No reason yet',
    align: 'end',
    cell: (r) => ({ text: String(r.notSet), ...(r.notSet ? { look: 'bad' as const } : {}) }),
  },
  { id: 'rate', label: 'Booking rate', align: 'end', cell: (r) => ({ text: rate(r.rate) }) },
];

/** Why they called, in a sentence: the period, and how many more were missed. */
export const reasonsLine = (p: CallReasonsProps): string =>
  `${p.label}, by the reason the office set. ${plural(p.missed, 'more was', 'more were')} missed.`;

// ── Over time ──

export type TrendLine = CampaignRow & { id: string };

/** The periods, newest first, and the total ("All of it"). */
export function trendLines(p: AdsTrendProps): { rows: TrendLine[]; total: TrendLine } {
  return {
    rows: [...p.rows].reverse().map((r) => ({
      ...r,
      id: r.period ?? r.label,
      label: periodWord(p.unit, r.period, r.label),
    })),
    total: { ...p.total, id: 'total', label: 'All of it' },
  };
}

/** Each period's columns: its calls (and the repeat ones), leads, bookings, revenue, spend and a lead's cost. */
export function trendColumns(unit: 'day' | 'week'): DataColumn<TrendLine>[] {
  return [
    {
      id: 'period',
      label: unit === 'week' ? 'Week' : 'Day',
      rowHeader: true,
      cell: (r) => ({ text: r.label }),
    },
    { id: 'calls', label: 'Calls', align: 'end', cell: (r) => count(r.calls) },
    {
      id: 'repeat',
      label: 'Of them repeat',
      align: 'end',
      cell: (r) => ({ text: r.repeatCalls == null ? '—' : String(r.repeatCalls) }),
    },
    { id: 'leads', label: 'Leads', align: 'end', cell: (r) => count(r.leads) },
    { id: 'booked', label: 'Booked', align: 'end', cell: (r) => count(r.booked) },
    { id: 'revenue', label: 'Revenue', align: 'end', cell: (r) => ({ text: money(r.revenueCents) }) },
    { id: 'spent', label: 'Spent', align: 'end', cell: spentCell },
    { id: 'cpl', label: 'Cost a lead', align: 'end', cell: (r) => moneyOrDash(r.costPerLeadCents) },
  ];
}

/** Whether a range has anything to show: calls, leads, revenue or spend on any of its days. */
export const trendHasAny = (p: AdsTrendProps): boolean =>
  p.rows.some((r) => r.calls || r.leads || r.revenueCents || r.spendCents);

// ── Today's calls ──

export type CallLine = CallsBySourceProps['calls'][number];

/** Who a call is from, by name when it matched a customer: the one it's on, or its first match. */
export function callerOf(c: CallLine): { name: string | null; customerId: string | null } {
  const name =
    c.matches.find((m) => m.customerId === c.customerId)?.customerName ?? c.matches[0]?.customerName ?? null;
  return { name, customerId: name ? (c.customerId ?? c.matches[0]?.customerId ?? null) : null };
}

/** Each call's columns: its time (red when missed and not texted back), who called (the way in to the
 * customer, its campaign and number under it), the reason, and what came of it (who took it under). */
export function callColumns(timeZone: string): DataColumn<CallLine>[] {
  return [
    {
      id: 'time',
      label: 'Time',
      cell: (c) => ({
        text: clock(c.startedAt, timeZone),
        look: callOutcome(c)[1] === 'bad' ? 'bad' : 'muted',
      }),
    },
    {
      id: 'caller',
      label: 'Caller',
      way: true,
      cell: (c) => {
        const who = callerOf(c).name;
        return {
          text: who ?? phone(c.from),
          lines: [
            {
              text: `${c.campaign ? campaignWord(c.campaign) : 'No campaign'}${who ? `. ${phone(c.from)}` : ''}${
                c.trackingLabel ? `, rang ${c.trackingLabel}` : ''
              }`,
            },
          ],
        };
      },
    },
    {
      id: 'reason',
      label: 'Reason',
      cell: (c) =>
        c.reason
          ? { text: c.reason.name }
          : {
              text: c.outcome === 'booked' || c.status === 'in_progress' ? '—' : 'Not set yet',
              look: 'muted',
            },
    },
    {
      id: 'outcome',
      label: 'What came of it',
      cell: (c) => {
        const [word, kind] = callOutcome(c);
        const under = c.textedBackAt
          ? `Texted back at ${clock(c.textedBackAt, timeZone)}`
          : c.takenBy
            ? `Taken by ${c.takenBy}`
            : null;
        return {
          word: { word, tone: toneOf(kind) },
          ...(under ? { lines: [{ text: under }] } : {}),
        };
      },
    },
  ];
}

/** What went to Google: one row each, its job (opening it) or the lead call, what was sent and Google's answer. */
export function conversionRows(p: ConversionsProps, openJob: (id: string) => void): Row[] {
  return p.conversions.map((c) => {
    const [word, kind] = CONVERSION[c.status];
    const sent = c.sentAt
      ? `. Sent ${dayOf(c.sentAt, p.timeZone)}, ${clock(c.sentAt, p.timeZone)}`
      : c.status === 'refused'
        ? `. Tried ${plural(c.attempts, 'time')}`
        : '';
    return {
      id: c.id,
      name: c.job ? c.job.number : 'Lead call',
      ...(c.job ? { onOpen: () => openJob(c.job!.id) } : {}),
      lines: [
        {
          text: `${c.job ? `${CONVERSION_WORDS[c.kind]}, from` : 'From'} a Google ${c.method}${
            c.valueCents !== null ? `, ${money(c.valueCents)}` : ''
          }${sent}${since(c, p.timeZone)}`.replace('.. ', '. '),
        },
        ...(c.lastError ? [{ text: c.lastError }] : []),
      ],
      end: { word, tone: toneOf(kind) },
    };
  });
}

/** What's sent to Google, in a sentence, with how many in all when there are more than shown. */
export const conversionsLine = (p: ConversionsProps): string =>
  `What came from a Google ad, each under its own conversion action: a lead call, a job’s first quote, a job booked and completed with its invoices before tax. Each job goes to an action once; one AdTrack already sent is kept back.${
    p.total !== undefined && p.total > p.conversions.length ? ` ${p.total} in all.` : ''
  }`;

// ── Suggested ad changes ──

/** The queue, one row a change: what (opening it), where, who prepared or decided it, and where it stands. */
export function adChangeRows(
  changes: readonly AdChangeRow[],
  timeZone: string,
  open: (id: string) => void,
): Row[] {
  return changes.map((c) => {
    const [word, kind] = changeState(c);
    const d = c.decided;
    return {
      id: c.id,
      name: c.title,
      onOpen: () => open(c.id),
      lines: [
        {
          text: `${channelWord(c.platform)}, ${c.target}. ${
            d
              ? `${c.status === 'dismissed' ? 'Dismissed' : 'Approved'} by ${d.by}, ${dayOf(d.at, timeZone)}`
              : `Prepared by ${c.preparedBy.name}, ${dayOf(c.preparedAt, timeZone)}`
          }`,
        },
      ],
      end: { word, tone: toneOf(kind) },
    };
  });
}

/** One change's facts: why, what changes, and who prepared it when. */
export const adChangeFacts = (c: AdChangeRow, timeZone: string): KeyValueItem[] => [
  { label: 'Why', value: c.why },
  { label: 'What changes', value: c.effect },
  { label: 'Prepared by', value: `${c.preparedBy.name}, ${at(c.preparedAt, timeZone)}` },
];

/** Its figures, at most four, as the sheet's rows: money as money. */
export const adChangeFigures = (c: AdChangeRow): KeyValueItem[] =>
  c.figures.slice(0, 4).map((f) => ({
    label: f.label,
    value: f.unit === 'money' ? money(f.value) : String(f.value),
  }));

/** What was decided, in sentences: who and when, the note, and what's left to do in the platform. */
export function adChangeDecided(c: AdChangeRow, timeZone: string): string[] {
  const d = c.decided;
  if (!d) return [];
  const where = channelWord(c.platform);
  return [
    // a time ends in "a.m.": its own dot ends the sentence
    `${c.status === 'dismissed' ? 'Dismissed' : 'Approved'} by ${d.by}, ${at(d.at, timeZone).replace(/\.$/, '')}.`,
    ...(d.note ? [`${c.status === 'dismissed' ? 'Why not' : 'Note'}: “${d.note}”`] : []),
    ...(c.status === 'approved'
      ? [`Make it in ${where} by hand: Flowology doesn’t change the ad account.`]
      : []),
    ...(c.done ? [`Made in ${where}: ${c.done.by} marked it done, ${at(c.done.at, timeZone)}`] : []),
  ];
}
